import { Component, inject, input, output, signal } from '@angular/core';

import { PlanningTone, TimelineDragSource, TimelineTrayItem } from '../../../models';
import { TimelineDragService } from '../../../services/timeline-drag.service';

@Component({
  selector: 'app-timeline-tray',
  templateUrl: './timeline-tray.component.html',
  styleUrl: './timeline-tray.component.scss',
})
// The waiting list beside an <app-timeline-board>: items not on any row yet. Drag one onto a
// row to place it; drag a bar back here to take it off the board. Production plan, 2026-10-26.
//
// Like the board it only reports - `returned` says a bar was dropped here, and the caller
// decides what that means.
export class TimelineTrayComponent {
  protected readonly drag = inject(TimelineDragService);
  protected readonly dragSource = TimelineDragSource;

  public readonly items = input<TimelineTrayItem[]>([]);
  public readonly header = input($localize`Waiting`);
  public readonly emptyText = input($localize`Nothing waiting.`);
  public readonly dropHint = input($localize`Drop here to take it off the board`);

  public readonly returned = output<string>();
  public readonly itemClicked = output<TimelineTrayItem>();

  protected readonly dropActive = signal(false);

  protected toneClass(item: TimelineTrayItem): string {
    return `plan-tone-${item.tone ?? PlanningTone.neutral}`;
  }

  protected onDragStart(event: DragEvent, item: TimelineTrayItem) {
    this.drag.start({ itemId: item.id, source: TimelineDragSource.tray }, event);
  }

  protected onDragEnd() {
    this.drag.end();
    this.dropActive.set(false);
  }

  // Only a bar coming OFF the board can be dropped here - re-dropping a tray item on the tray
  // would mean nothing.
  protected onDragOver(event: DragEvent) {
    if (this.drag.current()?.source !== TimelineDragSource.bar) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    this.dropActive.set(true);
  }

  protected onDragLeave(event: DragEvent, tray: HTMLElement) {
    if (!tray.contains(event.relatedTarget as Node)) {
      this.dropActive.set(false);
    }
  }

  protected onDrop(event: DragEvent) {
    const item = this.drag.current();
    this.dropActive.set(false);

    if (item?.source !== TimelineDragSource.bar) {
      return;
    }

    event.preventDefault();
    this.drag.end();
    this.returned.emit(item.itemId);
  }
}
